import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { IconUsers, IconBooks, IconCertificate, IconSchool, IconBook2, IconBrandYoutube } from '@tabler/icons-react';

gsap.registerPlugin(ScrollTrigger);

export function StatsSection() {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const cardsRef = useRef([]);

  const stats = [
    {
      id: 1,
      label: 'Popular Videos',
      value: '120+',
      icon: <IconBrandYoutube size={32} stroke={1.5} />,
      color: 'bg-blue-100 text-blue-600',
    },
    {
      id: 2,
      label: 'Magazines',
      value: '4+',
      icon: <IconBook2 size={32} stroke={1.5} />,
      color: 'bg-pumpkinToast-100 text-pumpkinToast-600',
    },
    {
      id: 3,
      label: 'Own Books',
      value: '6+',
      icon: <IconBooks size={32} stroke={1.5} />,
      color: 'bg-green-100 text-green-600',
    },
    {
      id: 4,
      label: 'Popular Courses',
      value: '3+',
      icon: <IconSchool size={32} stroke={1.5} />,
      color: 'bg-purple-100 text-purple-600',
    },
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Title Animation
      gsap.fromTo(
        titleRef.current,
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 85%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Cards Animation
      cardsRef.current.forEach((card, index) => {
        gsap.fromTo(
          card,
          { y: 50, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.6,
            delay: index * 0.15, // Stagger effect
            ease: 'back.out(1.7)',
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top 75%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="w-full py-16 sm:py-20 bg-white relative overflow-hidden"
    >
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-gray-50 rounded-full blur-3xl opacity-60 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-pumpkinToast-50/40 rounded-full blur-3xl opacity-60 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 relative z-10 text-center">
        {/* Header */}
        <div ref={titleRef} className="mb-12 sm:mb-16 max-w-2xl mx-auto">
          <span className="inline-block py-1 px-3 rounded-full bg-pumpkinToast-50 text-pumpkinToast-600 text-xs font-bold tracking-wider uppercase mb-3 border border-pumpkinToast-100">
            Our Impact
          </span>
          <h2 className="font-AndoraModernSerif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2a1273] mb-4">
            Empowering Through Knowledge
          </h2>
          <p className="text-gray-600 text-base sm:text-lg">
            Dedicated to spreading authentic Islamic teachings and fostering academic excellence.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {stats.map((stat, index) => (
            <div
              key={stat.id}
              ref={(el) => (cardsRef.current[index] = el)}
              className="group p-6 sm:p-8 bg-white rounded-2xl border border-gray-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_10px_30px_rgba(0,0,0,0.06)] transition-all duration-300 hover:-translate-y-1"
            >
              <div
                className={`w-14 h-14 mx-auto rounded-xl flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-110 ${stat.color}`}
              >
                {stat.icon}
              </div>
              <h3 className="font-AndoraModernSerif text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                {stat.value}
              </h3>
              <p className="text-gray-500 font-medium text-sm uppercase tracking-wide">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
